import React from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import MetricCard from '../../components/common/MetricCard';
import TimelineChart from './components/TimelineChart';
import { BarChart2, TrendingUp, Users, Mail } from 'lucide-react';
import Alert from '../../components/common/Alert';

const formatNumber = (num) => {
  if (!num) return '0';
  return num.toLocaleString();
};

const formatTime = (seconds) => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

export default function Analytics() {
  const { metrics, isLoading, error, hasAnalyticsAccess } = useAnalytics();

  console.log('Analytics render:', { metrics, isLoading, error });

  if (!hasAnalyticsAccess) {
    return (
      <Alert type="error" title="Access Denied">
        You don't have permission to view analytics data.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-ebony">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert type="error" title="Error loading analytics">
        {error.message || 'Failed to load analytics data. Please try again later.'}
      </Alert>
    );
  }
  console.log('Analytics render metrics:', metrics);
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-midnight font-heading">Email Analytics</h1>
        <div className="text-sm text-ebony">Last 30 days</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Emails Card */}
        <MetricCard
          title="Total Emails"
          subtitle="Last 30 days"
          icon={<BarChart2 className="text-sky-blue" />}
          tooltip="Total number of emails you've sent in the last 30 days"
        >
          <div className="text-3xl font-bold text-violet">
            {formatNumber(metrics?.emailVolume)}
          </div>
        </MetricCard>

        {/* Success Rate Card */}
        <MetricCard
          title="Success Rate"
          subtitle="Verified Emails"
          icon={<TrendingUp className="text-spring-green" />}
          tooltip="Percentage of emails that were verified by recipients"
        >
          <div className="text-3xl font-bold text-spring-green">
            {`${(metrics?.verificationRate || 0).toFixed(1)}%`}
          </div>
          <div className="text-sm text-ebony">
            {formatTime(metrics?.avgVerificationTime)} avg. verification time
          </div>
        </MetricCard>

        {/* Unique Recipients Card */}
        <MetricCard
          title="Unique Recipients"
          subtitle="Verified Users"
          icon={<Users className="text-violet" />}
          tooltip="Number of unique recipients who have verified your emails"
        >
          <div className="text-3xl font-bold text-midnight">
            {formatNumber(metrics?.uniqueVerifiedUsers)}
          </div>
          <div className="text-sm text-ebony">
            of {formatNumber(metrics?.uniqueRecipients)} total emails
          </div>
        </MetricCard>

        {/* Daily Emails Card */}
        <MetricCard
          title="Daily Emails"
          subtitle="Last 24 hours"
          icon={<Mail className="text-sky-blue" />}
          tooltip="Number of emails sent in the last 24 hours"
        >
          <div className="text-3xl font-bold text-violet">
            {formatNumber(metrics?.dailyEmails)}
          </div>
        </MetricCard>
      </div>

      {/* Timeline Chart */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-midnight font-heading">Email Activity Timeline</h2>
          <p className="text-sm text-ebony">Sent vs Verified emails over time</p>
        </div>
        <TimelineChart 
          data={metrics?.timelineData} 
          isLoading={isLoading}
        />
      </div>

      {/* Additional Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-midnight font-heading">Verification Insights</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-ebony">Average Time to Verify</span>
              <span className="font-medium text-midnight">{formatTime(metrics?.avgVerificationTime)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-ebony">Verified Recipients</span>
              <span className="font-medium text-midnight">{formatNumber(metrics?.uniqueVerifiedUsers)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-ebony">Total Recipients</span>
              <span className="font-medium text-midnight">{formatNumber(metrics?.uniqueRecipients)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}